import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Header from "@/components/Header";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Lobby from "@/pages/Lobby";
import Game from "@/pages/Game";
import Replay from "@/pages/Replay";
import ChallengeOpen from "@/pages/ChallengeOpen";
import Watch from "@/pages/Watch";
import WatchGame from "@/pages/WatchGame";
import Profile from "@/pages/Profile";
import PlayerProfile from "@/pages/PlayerProfile";
import FriendChat from "@/pages/FriendChat";
import Messages from "@/pages/Messages";
import AvatarEditor from "@/pages/AvatarEditor";
import Welcome from "@/pages/Welcome";
import HowToPlay from "@/pages/HowToPlay";
import Daily from "@/pages/Daily";
import DailyArchive from "@/pages/DailyArchive";
import FeedbackInbox from "@/pages/FeedbackInbox";
import GameStats from "@/pages/GameStats";
import Poll from "@/pages/Poll";
import { Privacy, Terms } from "@/pages/Legal";
import { Toaster } from "@/components/ui/sonner";
import NativePush from "@/components/NativePush";
import FriendMessageToasts from "@/components/FriendMessageToasts";
import LiveGameJoin from "@/components/LiveGameJoin";

// Medusa maze is a test game for one account; loaded only when opened.
const Maze = React.lazy(() => import("@/pages/Maze"));
const Race = React.lazy(() => import("@/pages/Race"));
const Squads = React.lazy(() => import("@/pages/Squads"));
const Squads2 = React.lazy(() => import("@/pages/Squads2"));
const Det = React.lazy(() => import("@/pages/Det"));
const Four = React.lazy(() => import("@/pages/Four"));
// Campaign test (Jack, 2026-10-29): loaded only when opened.
const Campaign = React.lazy(() => import("@/pages/Campaign"));
const CampaignLand = React.lazy(() => import("@/pages/CampaignLand"));
const CampaignLevel = React.lazy(() => import("@/pages/CampaignLevel"));
// Raidaverse test (Jack, 2026-10-29): loaded only when opened.
const Raidaverse = React.lazy(() => import("@/pages/Raidaverse"));
const Letterheist = React.lazy(() => import("@/pages/Letterheist"));
const Mini = React.lazy(() => import("@/pages/Mini"));
// Vowel Prowl: the regular game with no POWs, testers only.
const VowelProwl = React.lazy(() => import("@/pages/VowelProwl"));
const UiTrial = React.lazy(() => import("@/pages/UiTrial"));

function Shell({ children }) {
  return (
    <div className="App min-h-screen bg-background text-foreground">
      <Header />
      {children}
    </div>
  );
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/lobby" replace /> : <Landing />;
}

// A shared game link (/g/<id>) normally reaches the server for its preview
// first; if it lands here instead, go straight to the step-through.
function SharedGameRedirect() {
  const { matchId } = useParams();
  return <Navigate to={`/watch/${matchId}`} replace />;
}

// A fresh game page for each match. Going from one game straight to another
// (a "your turn" notification tapped while in a game) changes only the
// :matchId, and the old page kept the last game's board, names and socket
// while it loaded the new one, reloading the board several times.
function MatchPage() {
  const { matchId } = useParams();
  return <Game key={matchId} />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <NativePush />
          <FriendMessageToasts />
          <LiveGameJoin />
          <Routes>
            {/* Full-screen match route — no app header chrome */}
            <Route
              path="/match/:matchId"
              element={
                <ProtectedRoute>
                  <MatchPage />
                </ProtectedRoute>
              }
            />
            {/* Daily puzzle: full screen like a match; its archive has the header */}
            <Route
              path="/daily/archive"
              element={
                <Shell>
                  <ProtectedRoute>
                    <DailyArchive />
                  </ProtectedRoute>
                </Shell>
              }
            />
            {["/daily", "/daily/:n"].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <ProtectedRoute>
                    <Daily />
                  </ProtectedRoute>
                }
              />
            ))}
            {/* Mini daily puzzle: public, full screen, no account needed (Jack, 2026-10-26) */}
            <Route
              path="/mini"
              element={
                <React.Suspense fallback={null}>
                  <Mini />
                </React.Suspense>
              }
            />
            {/* Medusa maze test game: full screen, maze_test accounts only */}
            <Route
              path="/maze"
              element={
                <ProtectedRoute>
                  <React.Suspense fallback={null}>
                    <Maze />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />
            {/* 4-player Fortress: admins start games, invited players join (backend/four.py) */}
            <Route
              path="/four"
              element={
                <ProtectedRoute>
                  <React.Suspense fallback={null}>
                    <Four />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />
            {/* Det test: numbered tiles, det_test accounts only (admin) */}
            <Route
              path="/det"
              element={
                <ProtectedRoute>
                  <React.Suspense fallback={null}>
                    <Det />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />
            {/* Squads test game: full screen, squads_test accounts only (Jack) */}
            <Route
              path="/squads"
              element={
                <ProtectedRoute>
                  <React.Suspense fallback={null}>
                    <Squads />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />
            {/* Campaign: single-player levels, campaign_test accounts only (Jack) */}
            <Route
              path="/campaign"
              element={
                <ProtectedRoute>
                  <React.Suspense fallback={null}>
                    <Campaign />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/campaign/land/:landId"
              element={
                <ProtectedRoute>
                  <React.Suspense fallback={null}>
                    <CampaignLand />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/campaign/play/:levelId"
              element={
                <ProtectedRoute>
                  <React.Suspense fallback={null}>
                    <CampaignLevel />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />
            {/* Raidaverse: build your own half of the board, raidaverse_test accounts only (Jack) */}
            <Route
              path="/raidaverse"
              element={
                <ProtectedRoute>
                  <React.Suspense fallback={null}>
                    <Raidaverse />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />
            {/* Squads 2.0: chess pieces on 8x8, squads2_test accounts only (Jack) */}
            <Route
              path="/squads2"
              element={
                <ProtectedRoute>
                  <React.Suspense fallback={null}>
                    <Squads2 />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />
            {/* Letterheist: board-game-styled Jangle for older players,
                letterheist_test accounts only (Jack, 2026-10-24) */}
            <Route
              path="/letterheist"
              element={
                <ProtectedRoute>
                  <React.Suspense fallback={null}>
                    <Letterheist />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />
            {/* Arena, Stream and Minimal: trial game-screen looks vs the bots, maze_test accounts only (Jack, 2026-11-03) */}
            {["arena", "stream", "minimal"].map((k) => (
              <Route
                key={k}
                path={`/${k}`}
                element={
                  <ProtectedRoute>
                    <React.Suspense fallback={null}>
                      <UiTrial kind={k} />
                    </React.Suspense>
                  </ProtectedRoute>
                }
              />
            ))}
            {/* Vowel Prowl: no-POW boards vs the bots, maze_test accounts only */}
            <Route
              path="/vowel-prowl"
              element={
                <ProtectedRoute>
                  <React.Suspense fallback={null}>
                    <VowelProwl />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />
            {/* Raider Races test game: full screen, maze_test accounts only */}
            <Route
              path="/race"
              element={
                <ProtectedRoute>
                  <React.Suspense fallback={null}>
                    <Race />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />
            {/* A tapped challenge notification: accept and open the game */}
            <Route path="/challenge/:id" element={<ProtectedRoute><ChallengeOpen /></ProtectedRoute>} />
            <Route path="/race-invite/:id" element={<ProtectedRoute><ChallengeOpen race3 /></ProtectedRoute>} />
            <Route
              path="/replay/:matchId"
              element={
                <ProtectedRoute>
                  <Replay />
                </ProtectedRoute>
              }
            />
            {/* All other routes share the app shell with header */}
            <Route
              path="/*"
              element={
                <Shell>
                  <Routes>
                    <Route path="/" element={<RootRedirect />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/poll" element={<Poll />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route
                      path="/lobby"
                      element={
                        <ProtectedRoute>
                          <Lobby />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/watch"
                      element={
                        <ProtectedRoute>
                          <Watch />
                        </ProtectedRoute>
                      }
                    />
                    {/* Finished games open for anyone with the link (shared games) */}
                    <Route path="/watch/:matchId" element={<WatchGame />} />
                    <Route path="/g/:matchId" element={<SharedGameRedirect />} />
                    {/* The old Dashboard became Watch games. */}
                    <Route path="/dashboard" element={<Navigate to="/watch" replace />} />
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <Profile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/player/:id"
                      element={
                        <ProtectedRoute>
                          <PlayerProfile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/messages"
                      element={
                        <ProtectedRoute>
                          <Messages />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/friends/:id"
                      element={
                        <ProtectedRoute>
                          <FriendChat />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/avatar"
                      element={
                        <ProtectedRoute>
                          <AvatarEditor />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/welcome"
                      element={
                        <ProtectedRoute>
                          <Welcome />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/feedback"
                      element={
                        <ProtectedRoute>
                          <FeedbackInbox />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/stats"
                      element={
                        <ProtectedRoute>
                          <GameStats />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/how-to-play" element={<HowToPlay />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Shell>
              }
            />
          </Routes>
          <Toaster richColors position="top-center" />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
